import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { Button } from '@openai/apps-sdk-ui/components/Button';
import { Input } from '@openai/apps-sdk-ui/components/Input';
import {
  ArrowRight,
  ArrowUp,
  Terminal as TerminalIcon,
  Search,
  Keyboard as KeyboardIcon,
} from '@openai/apps-sdk-ui/components/Icon';
import { COMMANDS, completeCommand } from '../shared/commands';
import type { CommandContext } from '../shared/commands';
import { Kbd } from './ui';

export type { CommandContext } from '../shared/commands';
export type CommandEntry = {
  id: number;
  command: string;
  result: string;
  error?: boolean;
  time: string;
};
export function CommandBar({
  context,
  value,
  setValue,
  run,
  history,
  inputRef,
  busy,
  compact = false,
  connected = true,
}: {
  context: CommandContext;
  value: string;
  setValue: (value: string) => void;
  run: (command: string) => Promise<void>;
  history: string[];
  inputRef?: RefObject<HTMLInputElement | null>;
  busy: boolean;
  compact?: boolean;
  connected?: boolean;
}) {
  const ownRef = useRef<HTMLInputElement>(null);
  const ref = inputRef || ownRef;
  const [focused, setFocused] = useState(false);
  const [selection, setSelection] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const historyPosition = useRef(-1);
  const historyDraft = useRef('');
  const recalledValue = useRef<string | null>(null);
  const suggestions = useMemo(() => completeCommand(value, context).slice(0, 7), [value, context]);
  const showSuggestions = focused && !dismissed && !busy && suggestions.length > 0;
  useEffect(() => {
    setSelection(0);
    if (recalledValue.current === value) setDismissed(true);
    else {
      historyPosition.current = -1;
      setDismissed(false);
    }
  }, [value]);
  function edit(next: string) {
    historyPosition.current = -1;
    recalledValue.current = null;
    setValue(next);
  }
  function accept(index = selection) {
    const suggestion = suggestions[index];
    if (suggestion) {
      edit(suggestion.input);
      ref.current?.focus();
    }
  }
  async function submit() {
    if (!value.trim() || busy) return;
    setDismissed(true);
    historyPosition.current = -1;
    recalledValue.current = null;
    try {
      await run(value);
    } catch {
      /* The workspace reports command errors. */
    }
  }
  function keyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setDismissed(true);
      ref.current?.blur();
    } else if (event.key === 'Tab' && showSuggestions) {
      event.preventDefault();
      accept();
    } else if (
      event.key === 'ArrowUp' &&
      history.length &&
      (historyPosition.current >= 0 || !value.trim())
    ) {
      event.preventDefault();
      if (historyPosition.current < 0) historyDraft.current = value;
      const next = Math.min(historyPosition.current + 1, history.length - 1);
      historyPosition.current = next;
      const recalled = history[history.length - 1 - next];
      recalledValue.current = recalled;
      setValue(recalled);
      setDismissed(true);
    } else if (event.key === 'ArrowDown' && historyPosition.current >= 0) {
      event.preventDefault();
      const next = historyPosition.current - 1;
      historyPosition.current = next;
      const recalled = next < 0 ? historyDraft.current : history[history.length - 1 - next];
      recalledValue.current = recalled;
      setValue(recalled);
      setDismissed(true);
    } else if (event.key === 'ArrowDown' && showSuggestions) {
      event.preventDefault();
      setSelection((v) => (v + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp' && showSuggestions) {
      event.preventDefault();
      setSelection((v) => (v - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      void submit();
    }
  }
  return (
    <div className={`command-dock ${compact ? 'compact' : ''}`}>
      <div className="command-box">
        {showSuggestions && (
          <div
            className="command-suggestions"
            role="listbox"
            aria-label="Command suggestions"
            id="command-suggestions"
          >
            <div className="suggestion-heading">
              <span>COMMANDS</span>
              <span>
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd> navigate <Kbd>tab</Kbd> complete
              </span>
            </div>
            {suggestions.map((suggestion, index) => (
              <button
                id={`suggestion-${index}`}
                role="option"
                aria-selected={selection === index}
                key={suggestion.input}
                className={`suggestion ${selection === index ? 'selected' : ''}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => accept(index)}
              >
                <TerminalIcon />
                <span>
                  <code>{suggestion.label}</code>
                  <small>{suggestion.detail}</small>
                </span>
                <Kbd>tab</Kbd>
              </button>
            ))}
          </div>
        )}
        <div className={`command-line ${focused ? 'focused' : ''}`}>
          <span className="command-prompt" aria-hidden="true">
            ❯
          </span>
          <Input
            ref={ref}
            className="command-input"
            aria-label="Terminal command"
            role="combobox"
            aria-expanded={showSuggestions}
            aria-autocomplete="list"
            aria-controls="command-suggestions"
            aria-activedescendant={showSuggestions ? `suggestion-${selection}` : undefined}
            value={value}
            onChange={(event) => edit(event.target.value)}
            onFocus={() => {
              setFocused(true);
              setDismissed(false);
            }}
            onBlur={() => setFocused(false)}
            onKeyDown={keyDown}
            placeholder="Type a command…"
            autoComplete="off"
            spellCheck={false}
            readOnly={busy}
            aria-busy={busy}
          />
          <Button
            color="secondary"
            variant="ghost"
            uniform
            size="sm"
            aria-label="Run command"
            onClick={() => void submit()}
            disabled={!value.trim() || busy}
            loading={busy}
          >
            <ArrowRight />
          </Button>
        </div>
        <div className="command-caption">
          <span>
            <span className={`status-dot ${connected ? 'connected' : 'disconnected'}`} />
            {connected ? 'Workspace command line' : 'Connection interrupted — commands may fail'}
          </span>
          <span>
            <Kbd>⌘ K</Kbd> focus <span className="caption-dot">·</span>
            <Kbd>↵</Kbd> run <span className="caption-dot">·</span>
            <Kbd>tab</Kbd> complete
          </span>
        </div>
      </div>
    </div>
  );
}
export function TerminalPage({
  entries,
  insertCommand,
  connected = true,
  openAssistant,
}: {
  entries: CommandEntry[];
  insertCommand: (command: string) => void;
  connected?: boolean;
  openAssistant?: () => void;
}) {
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [entries]);
  return (
    <div className="terminal-page page-enter">
      <div className="page-heading">
        <div>
          <span className="eyebrow">LESS CLICKING. KEEP MOVING.</span>
          <h1>Terminal</h1>
          <p>Run commands. See what happens. Stay in flow.</p>
        </div>
        <span className="terminal-version">
          <span className={`status-dot ${connected ? 'connected' : 'disconnected'}`} />
          {connected ? 'Workspace ready' : 'Connection interrupted'}
        </span>
      </div>
      {openAssistant && (
        <Button color="secondary" variant="outline" size="sm" onClick={openAssistant}>
          Assistant review
        </Button>
      )}
      <div className="terminal-surface">
        <div className="terminal-titlebar">
          <span className="terminal-mini-prompt">❯_</span>
          <code>workspace / terminal</code>
          <span className="muted">
            {entries.length
              ? `${entries.length} command${entries.length === 1 ? '' : 's'} this session`
              : 'Ready when you are'}
          </span>
        </div>
        {!entries.length ? (
          <div className="terminal-welcome">
            <span className="large-prompt">❯_</span>
            <h2>Your next action starts here.</h2>
            <p>
              Type below. Terminal suggests the rest.
              <br />
              Press <Kbd>tab</Kbd> to complete and <Kbd>enter</Kbd> to run.
            </p>
            <div className="terminal-starters">
              {[
                ['task add ', 'Create a task'],
                ['task list', 'See your tasks'],
                ['help', 'Explore commands'],
              ].map(([command, label]) => (
                <button key={command} onClick={() => insertCommand(command)}>
                  <code>{command.trim()}</code>
                  <span>
                    {label}
                    <ArrowRight />
                  </span>
                </button>
              ))}
            </div>
            <p className="terminal-boundary">
              Commands act on this workspace. Every action is yours to run.
            </p>
          </div>
        ) : (
          <div className="terminal-transcript" role="log" aria-label="Command results">
            {entries.map((entry) => (
              <div className={`transcript-entry ${entry.error ? 'has-error' : ''}`} key={entry.id}>
                <div className="transcript-command">
                  <span>❯</span>
                  <code>{entry.command}</code>
                  <time>{entry.time}</time>
                </div>
                <pre>{entry.result}</pre>
              </div>
            ))}
            <div ref={end} />
          </div>
        )}
      </div>
    </div>
  );
}
const keyboardShortcuts = [
  ['⌘ / Ctrl + K', 'Focus the command line', 'global'],
  ['C', 'Create a task', 'outside text fields'],
  ['c, then Enter', 'Create a task', 'command line; add a title to create it straight away'],
  ['↑ / ↓', 'Navigate command suggestions', 'command line'],
  ['Tab', 'Accept the selected suggestion', 'command line'],
  ['Enter', 'Run the typed command', 'command line'],
  ['↑ / ↓', 'Browse command history', 'empty command line, then arrows'],
  ['Escape', 'Dismiss suggestions or close a dialog', 'global'],
  ['Enter', 'Send a message', 'conversation'],
  ['Shift + Enter', 'Add a line to a message', 'conversation'],
];
export function Shortcuts({ insertCommand }: { insertCommand: (value: string) => void }) {
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('commands');
  const filtered = COMMANDS.filter((command) =>
    `${command.usage} ${command.description} ${command.category}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );
  return (
    <div className="shortcuts-page page-enter">
      <div className="page-heading">
        <div>
          <span className="eyebrow">SMALL SHORTCUTS. BETTER FLOW.</span>
          <h1>Shortcuts</h1>
          <p>Your reference for commands and keyboard shortcuts.</p>
        </div>
        <div className="shortcut-art">
          <KeyboardIcon />
        </div>
      </div>
      <div className="shortcut-callout">
        <span className="command-prompt">❯</span>
        <div>
          <strong>Start typing. We’ll help with the rest.</strong>
          <p>Suggestions use your real tasks, channels, and teammates.</p>
        </div>
        <Kbd>tab</Kbd>
      </div>
      <div className="task-toolbar">
        <div className="tab-list">
          <button className={tab === 'commands' ? 'active' : ''} onClick={() => setTab('commands')}>
            Commands<span>{COMMANDS.length}</span>
          </button>
          <button className={tab === 'keyboard' ? 'active' : ''} onClick={() => setTab('keyboard')}>
            Keyboard shortcuts
          </button>
        </div>
        <Input
          startAdornment={<Search />}
          aria-label="Search shortcuts"
          placeholder="Find a shortcut…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="task-search"
        />
      </div>
      {tab === 'commands' ? (
        <div className="command-reference">
          {filtered.length === 0 && (
            <p className="muted no-results">No commands match “{query}”.</p>
          )}
          {[...new Set(filtered.map((command) => command.category))].map((category) => (
            <section key={category}>
              <h2 className="reference-category">{category}</h2>
              {filtered
                .filter((command) => command.category === category)
                .map((command) => (
                  <div className="reference-row" key={command.usage}>
                    <div>
                      <code>{command.usage}</code>
                      <p>{command.description}</p>
                    </div>
                    <Button
                      color="secondary"
                      variant="ghost"
                      size="sm"
                      onClick={() => insertCommand(command.example)}
                    >
                      Try it
                      <ArrowRight />
                    </Button>
                  </div>
                ))}
            </section>
          ))}
        </div>
      ) : (
        <div className="keyboard-reference">
          {keyboardShortcuts
            .filter((row) => row.join(' ').toLowerCase().includes(query.toLowerCase()))
            .map(([keys, description, context]) => (
              <div className="keyboard-row" key={description}>
                <div>
                  <strong>{description}</strong>
                  <small>{context}</small>
                </div>
                <Kbd>{keys}</Kbd>
              </div>
            ))}
        </div>
      )}
      <p className="reference-note">
        Commands run only when you press Enter. Suggested text is always yours to edit.
      </p>
    </div>
  );
}
